/* The calculator's price controls: one panel where every cotton in the mix
 * is priced, the single-product case's count and grade, and the small tag
 * that says whether a price is the market's or somebody's own.
 *
 * The rule the three share: a price follows the market until it is typed
 * over, typing is what switches it to manual, and the tag switches it back.
 * Nobody has to find a toggle before they can enter a number.
 */

import { useRef, useState, type CSSProperties } from 'react'
import { profitability } from '../../lib/api'
import type { CottonLot, Market, MarketYarn, Product, SinglePricing } from '../../lib/api'
import { Blueprint, LoadElsewhere, Note, NumberField, PanelHead, Segmented } from '../../components/ui'
import { useCan } from '../../lib/auth'
import { inr, num } from '../../lib/format'
import {
  countKey, dayMonthYear, findCount, findGrade, lotLabel, lotLetter, lotPrice, monthYear,
  monthsOld, yarnQuote,
} from './pricing'

const R: CSSProperties = { textAlign: 'right' }
const OWN = '__own__'

// ------------------------------------------------------------ source tag
/** Says where a price comes from, and flips it when pressed. */
export function SourceTag({ auto, estimated, available, title, onToggle }: {
  auto: boolean
  /** The market's figure is read off neighbouring counts, not published. */
  estimated?: boolean
  /** False when the market has no price to follow — the tag is then inert. */
  available: boolean
  title?: string
  onToggle: () => void
}) {
  const live = auto && available
  return (
    <button type="button" disabled={!available} onClick={onToggle}
      title={available
        ? `${title ? title + '. ' : ''}${live ? 'Press to type your own price' : 'Press to follow the market price'}`
        : 'No market price for this — enter your own'}
      className={`tag ${live ? (estimated ? 'tag-accent-2' : 'tag-accent') : 'tag-neutral'}`}
      style={{
        border: 0, cursor: available ? 'pointer' : 'default', whiteSpace: 'nowrap',
        fontFamily: 'inherit', minWidth: 58, justifyContent: 'center',
      }}>
      {live ? (estimated ? 'Auto · est.' : 'Auto') : 'Manual'}
    </button>
  )
}

const yarnTitle = (q: MarketYarn | null, market: Market | null) =>
  q ? `${inr(q.price)}/kg — ${q.basis}, ${q.note}, ${monthYear(market?.yarn.as_of)}` : undefined

// --------------------------------------------------------- cotton, in one place
export function CottonPanel({
  lots, products, market, candyKg, setLot, addLot, removeLot,
}: {
  lots: CottonLot[]
  /** The mix as resolved — used only to say which rows draw on each cotton. */
  products: Product[]
  market: Market | null
  candyKg: number
  setLot: (id: string, patch: Partial<CottonLot>) => void
  addLot: () => void
  removeLot: (id: string) => void
}) {
  const grades = market?.cotton.grades ?? []
  return (
    <Blueprint className="sheet no-print" style={{ padding: '20px 22px' }}>
      <PanelHead title="Cotton" right={
        <button className="btn btn-secondary" onClick={addLot}>Add cotton</button>
      } />
      <Note>
        Every cotton the mix is spun from is priced here and nowhere else — each product
        below only says which one it uses.{' '}
        {market?.cotton.as_of
          ? `Auto follows the CAI spot rate of ${dayMonthYear(market.cotton.as_of)}; type a price to use your own.`
          : 'No CAI rates on file, so prices are entered by hand.'}
      </Note>

      <div className="scroll-x">
        <table className="table" style={{ minWidth: 820 }}>
          <thead>
            <tr>
              <th>Cotton</th><th>CAI grade</th><th>Price</th>
              <th style={R}>₹ / candy</th><th style={R}>₹ / kg lint</th>
              <th>Used by</th><th />
            </tr>
          </thead>
          <tbody>
            {lots.map((lot, i) => {
              const { candy, live } = lotPrice(lot, market)
              const grade = findGrade(market, lot.grade)
              const users = products.filter((p) => p.cotton === lot.id)
              const mcs = users.reduce((n, p) => n + p.mcs, 0)
              return (
                <tr key={lot.id}>
                  <td style={{ fontWeight: 600 }}>{lotLetter(i)}</td>
                  <td>
                    <select value={grade ? grade.key : OWN} style={{ maxWidth: 250 }}
                      onChange={(e) => {
                        const g = findGrade(market, e.target.value)
                        setLot(lot.id, g
                          ? { grade: g.key, auto: true }
                          : { grade: null, auto: false, candy })
                      }}>
                      {grades.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
                      <option value={OWN}>Own price — not a CAI grade</option>
                    </select>
                  </td>
                  <td>
                    <SourceTag auto={lot.auto} available={Boolean(grade)}
                      title={grade ? `CAI ${grade.label}: ${inr(grade.candy)} on ${dayMonthYear(grade.date)}` : undefined}
                      // Leaving the market starts from the market's figure,
                      // so the price does not jump when the tag is pressed.
                      onToggle={() => setLot(lot.id, lot.auto
                        ? { auto: false, candy }
                        : { auto: true })} />
                  </td>
                  <td style={R}>
                    <NumberField value={candy} min={0} step={100} style={{ width: 110 }}
                      onChange={(v) => setLot(lot.id, { candy: v, auto: false })} />
                  </td>
                  <td className="num" style={R}>{inr(candy / candyKg, 1)}</td>
                  <td style={{ fontSize: 13, color: 'var(--color-neutral-700)' }}>
                    {users.length
                      ? `${users.map((p) => p.name).join(', ')} · ${num(mcs)} machines`
                      : 'No product yet'}
                    {live && live.date !== market?.cotton.as_of
                      ? ` · last fixed ${dayMonthYear(live.date)}` : ''}
                  </td>
                  <td style={R}>
                    <button className="btn btn-ghost" style={{ fontSize: 12 }}
                      disabled={users.length > 0 || lots.length < 2}
                      title={users.length ? 'Move its products to another cotton first' : undefined}
                      onClick={() => removeLot(lot.id)}>Remove</button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Blueprint>
  )
}

// ------------------------------------------------------- mix table cells
/** The count a row spins. Choosing one is what prices the row. */
export function CountSelect({ product, market, onPick }: {
  product: Product
  market: Market | null
  onPick: (count: number, quality: string) => void
}) {
  if (!market?.counts.length) return null
  return (
    <select value={countKey(product.count, product.quality)} style={{ width: 104 }}
      onChange={(e) => {
        const c = market.counts.find((x) => countKey(x.count, x.quality) === e.target.value)
        if (c) onPick(c.count, c.quality)
      }}>
      {countKey(product.count, product.quality) ? null : <option value="">Count…</option>}
      {market.counts.map((c) => (
        <option key={c.label} value={countKey(c.count, c.quality)}>{c.label}</option>
      ))}
    </select>
  )
}

export function YarnCell({ product, shown, market, setProduct }: {
  product: Product
  /** The price in force — the market's when the row follows it. */
  shown: number
  market: Market | null
  setProduct: (id: string, patch: Partial<Product>) => void
}) {
  const quote = yarnQuote(market, product)
  return (
    <td style={R}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <SourceTag auto={Boolean(product.yarnAuto)} available={Boolean(quote)}
          estimated={quote?.basis === 'estimated'} title={yarnTitle(quote, market)}
          onToggle={() => setProduct(product.id, product.yarnAuto
            ? { yarnAuto: false, yarn: shown }
            : { yarnAuto: true })} />
        <NumberField value={shown} min={0} step={1} style={{ width: 80 }}
          onChange={(v) => setProduct(product.id, { yarn: v, yarnAuto: false })} />
      </div>
    </td>
  )
}

export function CottonCell({ product, shown, lots, market, setProduct }: {
  product: Product
  shown: number
  lots: CottonLot[]
  market: Market | null
  setProduct: (id: string, patch: Partial<Product>) => void
}) {
  return (
    <td>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <select value={product.cotton ?? ''} style={{ width: 176 }}
          onChange={(e) => setProduct(product.id, { cotton: e.target.value })}>
          {lots.map((l, i) => <option key={l.id} value={l.id}>{lotLabel(l, i, market)}</option>)}
        </select>
        <span className="num" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{inr(shown)}</span>
      </div>
    </td>
  )
}

/** The count's SITRA output, offered when the case is producing like some
 *  other count. Choosing a count sets prices only — output is the mill's
 *  own figure to give — so a mismatch is pointed out rather than overwritten. */
export function standardGps(
  market: Market | null, row: { count?: number | null; quality?: string | null }, gps: number,
): number | null {
  const std = findCount(market, row.count, row.quality)?.gps
  return std && Math.abs(gps - std) / std > 0.1 ? std : null
}

export function GpsCell({ product, market, setProduct }: {
  product: Product
  market: Market | null
  setProduct: (id: string, patch: Partial<Product>) => void
}) {
  const std = standardGps(market, product, product.gps)
  return (
    <td style={R}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {std ? (
          <button type="button" className="tag tag-outline"
            title={`SITRA's standard for ${product.count}s ${product.quality} is ${std} g per spindle per shift. Press to use it.`}
            style={{ cursor: 'pointer', background: 'none', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
            onClick={() => setProduct(product.id, { gps: std })}>SITRA {std}</button>
        ) : null}
        <NumberField value={product.gps} min={20} max={400} step={5} style={{ width: 84 }}
          onChange={(v) => setProduct(product.id, { gps: v })} />
      </div>
    </td>
  )
}

// --------------------------------------------------- single-product prices
export function SinglePrices({ single, setSingle, market, yarn, candy, gps, useGps }: {
  single: SinglePricing
  setSingle: (patch: Partial<SinglePricing>) => void
  market: Market | null
  /** The two prices in force, for the read-outs. */
  yarn: number
  candy: number
  gps: number
  useGps: (v: number) => void
}) {
  if (!market) return null
  const quote = yarnQuote(market, single)
  const std = standardGps(market, single, gps)
  const grade = findGrade(market, single.grade)
  const line: CSSProperties = { fontSize: 12, color: 'var(--color-neutral-700)' }
  const cell: CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(330px,1fr))',
      gap: '14px 34px', marginTop: 14, paddingBottom: 16,
      borderBottom: '1px solid var(--color-divider)',
    }}>
      <div>
        <div style={cell}>
          <label className="field-label" style={{ minWidth: 46 }}>Count</label>
          <select value={countKey(single.count, single.quality)} style={{ width: 110 }}
            onChange={(e) => {
              const c = market.counts.find((x) => countKey(x.count, x.quality) === e.target.value)
              // A new count brings its own cotton, and both prices go back
              // to the market: the count is what they are being read for.
              if (c) {
                setSingle({
                  count: c.count, quality: c.quality, yarnAuto: Boolean(c.yarn),
                  grade: c.grade ?? single.grade,
                  cottonAuto: Boolean(findGrade(market, c.grade ?? single.grade)),
                })
              }
            }}>
            {countKey(single.count, single.quality) ? null : <option value="">Choose…</option>}
            {market.counts.map((c) => (
              <option key={c.label} value={countKey(c.count, c.quality)}>{c.label}</option>
            ))}
          </select>
          <Segmented<string> value={single.yarnAuto && quote ? 'auto' : 'manual'}
            onChange={(v) => setSingle({ yarnAuto: v === 'auto' && Boolean(quote) })}
            options={[{ value: 'auto', label: 'Auto yarn price' }, { value: 'manual', label: 'Manual' }]} />
        </div>
        <div style={{ ...line, marginTop: 6 }}>
          {single.yarnAuto && quote
            ? `Yarn ${inr(yarn)}/kg — ${quote.basis}, ${quote.note} · ${monthYear(market.yarn.as_of)}`
            : quote
              ? `Yarn ${inr(yarn)}/kg, typed. The sheet says ${inr(quote.price)} (${quote.basis}, ${monthYear(market.yarn.as_of)}).`
              : `Yarn ${inr(yarn)}/kg, typed. Choose a count to follow the price sheet.`}
        </div>
        {std ? (
          <div style={{ ...line, marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>
              Output per spindle is {num(gps)} g; SITRA's standard for {single.count}s {single.quality} is {std} g.
            </span>
            <button type="button" className="tag tag-outline"
              style={{ cursor: 'pointer', background: 'none', fontFamily: 'inherit' }}
              onClick={() => useGps(std)}>Use {std} g</button>
          </div>
        ) : null}
      </div>

      <div>
        <div style={cell}>
          <label className="field-label" style={{ minWidth: 46 }}>Cotton</label>
          <select value={grade ? grade.key : OWN} style={{ maxWidth: 240 }}
            onChange={(e) => {
              const g = findGrade(market, e.target.value)
              setSingle({ grade: g ? g.key : null, cottonAuto: Boolean(g) })
            }}>
            {market.cotton.grades.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
            <option value={OWN}>Own price — not a CAI grade</option>
          </select>
          <Segmented<string> value={single.cottonAuto && grade ? 'auto' : 'manual'}
            onChange={(v) => setSingle({ cottonAuto: v === 'auto' && Boolean(grade) })}
            options={[{ value: 'auto', label: 'Auto cotton price' }, { value: 'manual', label: 'Manual' }]} />
        </div>
        <div style={{ ...line, marginTop: 6 }}>
          {single.cottonAuto && grade
            ? `Cotton ${inr(candy)}/candy — CAI spot rate of ${dayMonthYear(grade.date)}`
            : grade
              ? `Cotton ${inr(candy)}/candy, typed. CAI says ${inr(grade.candy)} (${dayMonthYear(grade.date)}).`
              : `Cotton ${inr(candy)}/candy, typed.`}
        </div>
      </div>
    </div>
  )
}

// ----------------------------------------------------------- where from
/** One line on where the prices come from and how old they are, with the
 *  way to bring the yarn sheet up to date. */
export function MarketLine({ market, onLoaded }: { market: Market | null; onLoaded: () => void }) {
  const canUpload = useCan('upload')
  // An editor hands files in on the Data loads page instead.
  const loadElsewhere = useCan('load') && !canUpload
  const ref = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ text: string; bad?: boolean } | null>(null)
  const age = monthsOld(market?.yarn.as_of)

  const upload = async (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return
    setBusy(true)
    setMsg(null)
    try {
      const r = await profitability.uploadYarnPrices(file)
      setMsg({ text: `${r.source}: ${num(r.rows)} prices for ${r.series} yarns, through ${r.last_month}.` })
      onLoaded()
    } catch (e: any) {
      setMsg({ text: e?.message ?? String(e), bad: true })
    } finally {
      setBusy(false)
      if (ref.current) ref.current.value = ''
    }
  }

  return (
    <div className="no-print" style={{
      display: 'flex', alignItems: 'center', gap: '8px 14px', flexWrap: 'wrap',
      marginTop: 16, fontSize: 12, color: 'var(--color-neutral-700)',
    }}>
      <span>
        {market?.yarn.as_of
          ? <>Yarn prices: {market.yarn.source ?? 'count-wise sheet'}, through{' '}
            <b style={age > (market.fresh_months ?? 3) ? { color: 'var(--color-bad)' } : undefined}>
              {monthYear(market.yarn.as_of)}
            </b>
            {age > 1 ? ` — ${age} months old` : ''}.</>
          : 'No yarn price sheet on file — yarn prices are typed.'}
        {market?.cotton.as_of ? ` Cotton: CAI spot rates of ${dayMonthYear(market.cotton.as_of)}.` : ''}
      </span>
      {canUpload ? (
        <label className="tag tag-outline" style={{ cursor: busy ? 'default' : 'pointer' }}>
          {busy ? 'Reading…' : 'Load a newer yarn price sheet'}
          <input ref={ref} type="file" accept=".xlsx" disabled={busy}
            onChange={(e) => upload(e.target.files)} style={{ display: 'none' }} />
        </label>
      ) : null}
      {loadElsewhere ? <LoadElsewhere /> : null}
      {msg ? (
        <span style={{ color: msg.bad ? 'var(--color-bad)' : 'var(--color-good)' }}>{msg.text}</span>
      ) : null}
    </div>
  )
}
